import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  AlertTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Stack,
  LinearProgress,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import {
  Assignment as PBIIcon,
  Bolt as StoryPointIcon,
  CheckCircle as CompletionIcon,
  OpenInNew as OpenInNewIcon,
  ExpandLess as ExpandLessIcon,
  ChevronRight as ChevronRightIcon,
  Launch as LaunchIcon,
  CloudSync as WebhookIcon,
  ExpandMore as ExpandMoreIcon,
  Timeline as TimelineIcon,
  NewReleases as NewReleasesIcon,
  ViewModule as GroupByIcon,
  ViewList as FlatListIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip as ChartTooltip,
  Legend as ChartLegend
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { get } from '../../services/apiService';

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTooltip, ChartLegend, ChartDataLabels);

const DARK_GREEN = '#1D4434';
const ACCENT = '#1D4434';

function CompletionDonut({ percentage = 0, size = 40, thickness = 5 }) {
  const value = Math.min(Math.max(Number(percentage), 0), 100);
  const color = value < 33 ? 'error' : value < 67 ? 'warning' : 'success';
  return (
    <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress variant="determinate" value={value} color={color} size={size} thickness={thickness} />
      <Box sx={{ position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="caption" fontWeight="medium" color="text.primary">
          {Math.round(value)}%
        </Typography>
      </Box>
    </Box>
  );
}

function getAdoUrl(workItemId) {
  if (!workItemId) return '#';
  const adoOrg = process.env.REACT_APP_AZURE_DEVOPS_ORG || 'tr-legal.visualstudio.com';
  const projectName = process.env.REACT_APP_AZURE_DEVOPS_PROJECT || 'HighQ Tech';
  const baseUrl = adoOrg.includes('visualstudio.com')
    ? `https://${adoOrg}`
    : `https://dev.azure.com/${adoOrg}`;
  return `${baseUrl}/${encodeURIComponent(projectName)}/_workitems/edit/${workItemId}`;
}

function formatLastUpdated(dateString) {
  if (!dateString) return 'Never';
  try {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  } catch {
    return dateString;
  }
}

const STATE_CHIP_COLOR = (state) => {
  if (['Closed', 'Done', 'Completed'].includes(state)) return 'success';
  if (['Active', 'In Progress', 'Committed'].includes(state)) return 'primary';
  return 'default';
};

// 'Removed' is intentionally excluded — Removed items are discarded server-side.
const COMPLETED_STATES_SET = new Set(['Closed', 'Done', 'Completed']);
const NEWLY_INTRODUCED_DAYS = 30;

function isNewlyIntroduced(item) {
  if (!item.createdDate) return false;
  const ageDays = (Date.now() - new Date(item.createdDate)) / (1000 * 60 * 60 * 24);
  return ageDays <= NEWLY_INTRODUCED_DAYS && !COMPLETED_STATES_SET.has(item.state);
}

/**
 * Eng - Memory Improvements tab inside Production analysis.
 * Shows PBIs planned under ADO Feature 919722 (Memory Improvements).
 * Data is sourced from the shared workitems collection (ADO webhook pipeline).
 */
const MemoryImprovementsTab = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [items, setItems] = useState([]);
  const [releaseTrend, setReleaseTrend] = useState([]);
  const [trend, setTrend] = useState([]);
  const [stateFilter, setStateFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [releaseFilter, setReleaseFilter] = useState('');
  const [showNewlyIntroduced, setShowNewlyIntroduced] = useState(false);
  const [groupByRelease, setGroupByRelease] = useState(false);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [itemChildren, setItemChildren] = useState({});
  const [loadingChildren, setLoadingChildren] = useState(new Set());
  const [sprintModal, setSprintModal] = useState({ open: false, sprintLabel: '', type: '', tickets: [] });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [dashRes, itemsRes, trendRes, createdVsResolvedRes] = await Promise.all([
        get('/api/memory-improvements/dashboard'),
        get('/api/memory-improvements/items'),
        get('/api/memory-improvements/release-trend'),
        get('/api/memory-improvements/trend')
      ]);
      if (dashRes.success) setDashboard(dashRes.data);
      if (itemsRes.success) setItems(itemsRes.data || []);
      if (trendRes.success) setReleaseTrend(trendRes.data || []);
      if (createdVsResolvedRes.success) setTrend(createdVsResolvedRes.data || []);
    } catch (err) {
      console.error('Error loading Memory Improvements data:', err);
      setError(err.message || 'Failed to load Memory Improvements data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const toggleExpand = useCallback(async (pbiId) => {
    const id = String(pbiId);
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    if (!itemChildren[id]) {
      setLoadingChildren((prev) => new Set(prev).add(id));
      try {
        const res = await get(`/api/memory-improvements/items/${id}/children`);
        setItemChildren((prev) => ({ ...prev, [id]: res.data || [] }));
      } catch (err) {
        console.error('Error loading PBI children:', err);
        setItemChildren((prev) => ({ ...prev, [id]: [] }));
      } finally {
        setLoadingChildren((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }
    }
  }, [itemChildren]);

  const uniqueStates = useMemo(
    () => [...new Set(items.map((i) => i.state).filter(Boolean))].sort(),
    [items]
  );

  const uniqueClients = useMemo(
    () => [...new Set(items.map((i) => i.clientName).filter(Boolean))].sort(),
    [items]
  );

  const uniqueReleases = useMemo(() => {
    const releases = items.map((i) => i.fixVersion || 'Unscheduled');
    return [...new Set(releases)].sort((a, b) => {
      if (a === 'Unscheduled') return 1;
      if (b === 'Unscheduled') return -1;
      return String(b).localeCompare(String(a), undefined, { numeric: true });
    });
  }, [items]);

  const newlyIntroducedItems = useMemo(
    () => items.filter(isNewlyIntroduced),
    [items]
  );

  const filteredItems = useMemo(() => {
    let result = items;
    if (stateFilter) result = result.filter((i) => i.state === stateFilter);
    if (clientFilter) result = result.filter((i) => i.clientName === clientFilter);
    if (releaseFilter) result = result.filter((i) => (i.fixVersion || 'Unscheduled') === releaseFilter);
    if (showNewlyIntroduced) result = result.filter(isNewlyIntroduced);
    return result;
  }, [items, stateFilter, clientFilter, releaseFilter, showNewlyIntroduced]);

  // Items grouped by fixVersion for the grouped view
  const itemsByRelease = useMemo(() => {
    const groups = new Map();
    filteredItems.forEach((item) => {
      const key = item.fixVersion || 'Unscheduled';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(item);
    });
    return Array.from(groups.entries())
      .sort(([a], [b]) => {
        if (a === 'Unscheduled') return 1;
        if (b === 'Unscheduled') return -1;
        return String(b).localeCompare(String(a), undefined, { numeric: true });
      });
  }, [filteredItems]);

  const handleBarClick = useCallback((_event, elements) => {
    if (!elements.length) return;
    const { datasetIndex, index } = elements[0];
    const sprint = trend[index];
    if (!sprint) return;
    const type = datasetIndex === 0 ? 'created' : 'resolved';
    const start = new Date(sprint.sprintStart).getTime();
    const end = new Date(sprint.sprintEnd).getTime();
    const tickets = items.filter((item) => {
      if (type === 'created') {
        if (!item.createdDate) return false;
        const d = new Date(item.createdDate).getTime();
        return d >= start && d < end;
      } else {
        if (!COMPLETED_STATES_SET.has(item.state) || !item.closedDate) return false;
        const d = new Date(item.closedDate).getTime();
        return d >= start && d < end;
      }
    });
    setSprintModal({ open: true, sprintLabel: sprint.sprint, type, tickets });
  }, [trend, items]);

  // Chart data for created vs resolved trend
  const trendChartData = useMemo(() => ({
    labels: trend.map((d) => d.sprint),
    datasets: [
      {
        label: 'Created',
        data: trend.map((d) => d.created),
        backgroundColor: 'rgba(21, 101, 192, 0.82)',
        borderColor: '#1565C0',
        borderWidth: 1,
        borderRadius: 4,
        datalabels: {
          anchor: 'end',
          align: 'end',
          offset: 2,
          color: '#1565C0',
          font: { size: 10, weight: '700' },
          formatter: (v) => v > 0 ? v : ''
        }
      },
      {
        label: 'Resolved',
        data: trend.map((d) => d.resolved),
        backgroundColor: 'rgba(56, 142, 60, 0.82)',
        borderColor: '#388E3C',
        borderWidth: 1,
        borderRadius: 4,
        datalabels: {
          anchor: 'end',
          align: 'end',
          offset: 2,
          color: '#2E7D32',
          font: { size: 10, weight: '700' },
          formatter: (v) => v > 0 ? v : ''
        }
      }
    ]
  }), [trend]);

  const trendChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { top: 18 } },
    onHover: (event, elements) => {
      event.native.target.style.cursor = elements.length ? 'pointer' : 'default';
    },
    plugins: {
      legend: { position: 'top', labels: { boxWidth: 12, font: { size: 11 } } },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          footer: () => 'Click bar to see tickets'
        }
      },
      datalabels: { display: true }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 10 }, maxRotation: 35, minRotation: 25 }
      },
      y: {
        beginAtZero: true,
        ticks: { stepSize: 1, font: { size: 10 } },
        grid: { color: 'rgba(0,0,0,0.06)' }
      }
    }
  }), []);

  const totalPBIs = dashboard?.totalPBIs || 0;
  const completedPBIs = dashboard?.completedPBIs || 0;
  const totalStoryPoints = dashboard?.totalStoryPoints || 0;
  const completedStoryPoints = dashboard?.completedStoryPoints || 0;
  const remainingStoryPoints = Math.max(totalStoryPoints - completedStoryPoints, 0);
  const completion = dashboard?.avgCompletion || 0;
  const featureId = dashboard?.featureId;
  const featureTitle = dashboard?.featureTitle;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* Header */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={600}>
            Eng - Memory Improvements
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25 }} flexWrap="wrap">
            <Typography variant="body2" color="text.secondary">Feature:</Typography>
            {featureId ? (
              <Box
                component="a"
                href={getAdoUrl(featureId)}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.5,
                  color: 'primary.main', textDecoration: 'none', fontWeight: 500,
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                #{featureId} {featureTitle ? `— ${featureTitle}` : ''}
                <LaunchIcon sx={{ fontSize: '0.9rem' }} />
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">(not found in sync)</Typography>
            )}
          </Stack>
        </Box>
        <Tooltip title="Data flows through the shared ADO webhook pipeline into the common workitems collection — no separate sync is needed.">
          <Chip
            icon={<WebhookIcon sx={{ fontSize: 16 }} />}
            label={
              dashboard?.lastSync
                ? `Auto-synced from ADO webhooks · updated ${formatLastUpdated(dashboard.lastSync)}`
                : 'Auto-synced from ADO webhooks'
            }
            size="small"
            sx={{
              bgcolor: `${DARK_GREEN}10`, color: DARK_GREEN, fontWeight: 500,
              '& .MuiChip-icon': { color: DARK_GREEN }
            }}
          />
        </Tooltip>
      </Box>

      {/* Progress Cards */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <Card elevation={2} sx={{ height: '100%', background: `linear-gradient(135deg, ${ACCENT}08 0%, ${ACCENT}02 100%)`, border: `1px solid ${ACCENT}30` }}>
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                <Box sx={{ width: 48, height: 48, borderRadius: 1.5, bgcolor: `${ACCENT}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CompletionIcon sx={{ color: ACCENT, fontSize: 24 }} />
                </Box>
                <Box flex={1}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 500 }}>
                    Overall Completion
                  </Typography>
                  <Typography variant="h3" fontWeight={700} sx={{ color: ACCENT, lineHeight: 1.1, background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT}cc 100%)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                    {completion}%
                  </Typography>
                </Box>
                <CompletionDonut percentage={completion} size={56} thickness={4} />
              </Stack>
              <LinearProgress variant="determinate" value={completion} sx={{ height: 8, borderRadius: 1, bgcolor: `${ACCENT}15`, '& .MuiLinearProgress-bar': { bgcolor: ACCENT } }} />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Weighted by story points across {totalPBIs} PBI{totalPBIs === 1 ? '' : 's'}.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2} sx={{ height: '100%' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
                <Box sx={{ width: 40, height: 40, borderRadius: 1.5, bgcolor: '#1976d215', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <PBIIcon sx={{ color: '#1976d2', fontSize: 22 }} />
                </Box>
                <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  PBIs
                </Typography>
              </Stack>
              <Typography variant="h4" fontWeight={700}>
                {completedPBIs}
                <Typography component="span" variant="h5" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {' '}/ {totalPBIs}
                </Typography>
              </Typography>
              <Typography variant="caption" color="text.secondary">completed / total</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2} sx={{ height: '100%' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
                <Box sx={{ width: 40, height: 40, borderRadius: 1.5, bgcolor: '#ed6c0215', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <StoryPointIcon sx={{ color: '#ed6c02', fontSize: 22 }} />
                </Box>
                <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Story Points
                </Typography>
              </Stack>
              <Typography variant="h4" fontWeight={700}>
                {completedStoryPoints}
                <Typography component="span" variant="h5" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {' '}/ {totalStoryPoints}
                </Typography>
              </Typography>
              <Typography variant="caption" color="text.secondary">{remainingStoryPoints} remaining</Typography>
            </CardContent>
          </Card>
        </Grid>

      </Grid>

      {/* Created vs Resolved sprint trend chart */}
      {trend.length > 0 && (
        <Accordion
          elevation={1}
          defaultExpanded
          sx={{ mb: 1.5, borderRadius: 1, '&:before': { display: 'none' }, border: `1px solid ${ACCENT}20` }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ minHeight: 48, '& .MuiAccordionSummary-content': { my: 1 } }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center" flex={1} flexWrap="wrap">
              <TimelineIcon sx={{ color: ACCENT, fontSize: 20 }} />
              <Typography variant="subtitle1" fontWeight={600}>
                Created vs Resolved — bi-weekly sprints (Apr 2026 onwards)
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Box sx={{ height: 260 }}>
              <Bar data={trendChartData} options={trendChartOptions} onClick={handleBarClick} />
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Release-wise fix trend (collapsed by default) */}
      <Accordion
        elevation={1}
        sx={{
          mb: 1.5,
          borderRadius: 1,
          '&:before': { display: 'none' },
          border: `1px solid ${ACCENT}20`
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ minHeight: 48, '& .MuiAccordionSummary-content': { my: 1 } }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" flex={1} flexWrap="wrap">
            <TimelineIcon sx={{ color: ACCENT, fontSize: 20 }} />
            <Typography variant="subtitle1" fontWeight={600}>
              Release-wise fix trend
            </Typography>
            <Chip
              size="small"
              label={`${releaseTrend.length} release${releaseTrend.length === 1 ? '' : 's'}`}
              sx={{ bgcolor: `${ACCENT}15`, color: ACCENT, fontWeight: 500 }}
            />
            {releaseTrend.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                grouped by Fix Version
              </Typography>
            )}
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          {releaseTrend.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              No release data available yet. PBIs will appear here once they are tagged with a Fix Version in ADO.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Release</TableCell>
                    <TableCell align="right">PBIs (done / total)</TableCell>
                    <TableCell align="right">Story Points (done / total)</TableCell>
                    <TableCell sx={{ minWidth: 220 }}>Completion</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {releaseTrend.map((r) => (
                    <TableRow key={r.release} hover>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" fontWeight={500}>
                            {r.release}
                          </Typography>
                          {r.release === 'Unscheduled' && (
                            <Chip label="No fix version" size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        {r.completedPBIs} / {r.totalPBIs}
                      </TableCell>
                      <TableCell align="right">
                        {r.completedStoryPoints} / {r.totalStoryPoints}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <LinearProgress
                            variant="determinate"
                            value={r.completion}
                            sx={{
                              flex: 1,
                              height: 8,
                              borderRadius: 1,
                              bgcolor: `${ACCENT}15`,
                              '& .MuiLinearProgress-bar': { bgcolor: ACCENT }
                            }}
                          />
                          <Typography variant="caption" fontWeight={600} sx={{ minWidth: 36, textAlign: 'right' }}>
                            {r.completion}%
                          </Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Filters */}
      <Paper sx={{ p: 1.5, mb: 1.5, borderRadius: 1 }}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>State</InputLabel>
              <Select value={stateFilter} onChange={(e) => setStateFilter(e.target.value)} label="State">
                <MenuItem value="">All</MenuItem>
                {uniqueStates.map((s) => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Client</InputLabel>
              <Select value={clientFilter} onChange={(e) => setClientFilter(e.target.value)} label="Client">
                <MenuItem value="">All Clients</MenuItem>
                {uniqueClients.map((c) => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Fix Version</InputLabel>
              <Select value={releaseFilter} onChange={(e) => setReleaseFilter(e.target.value)} label="Fix Version">
                <MenuItem value="">All Releases</MenuItem>
                {uniqueReleases.map((r) => (
                  <MenuItem key={r} value={r}>{r}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm="auto">
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Tooltip title={`Items added in the last ${NEWLY_INTRODUCED_DAYS} days that are still active`}>
                <Chip
                  label={`Newly Introduced${newlyIntroducedItems.length > 0 ? ` (${newlyIntroducedItems.length})` : ''}`}
                  icon={<NewReleasesIcon />}
                  onClick={() => setShowNewlyIntroduced((v) => !v)}
                  color={showNewlyIntroduced ? 'warning' : 'default'}
                  variant={showNewlyIntroduced ? 'filled' : 'outlined'}
                  clickable
                  sx={{ fontWeight: showNewlyIntroduced ? 600 : 400 }}
                />
              </Tooltip>
              <Tooltip title={groupByRelease ? 'Switch to flat list' : 'Group items by Fix Version / Release'}>
                <Chip
                  label={groupByRelease ? 'Flat List' : 'Group by Release'}
                  icon={groupByRelease ? <FlatListIcon /> : <GroupByIcon />}
                  onClick={() => setGroupByRelease((v) => !v)}
                  color={groupByRelease ? 'primary' : 'default'}
                  variant={groupByRelease ? 'filled' : 'outlined'}
                  clickable
                  sx={{ fontWeight: groupByRelease ? 600 : 400 }}
                />
              </Tooltip>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Newly introduced critical instances banner */}
      {newlyIntroducedItems.length > 0 && (
        <Alert
          severity="warning"
          icon={<NewReleasesIcon />}
          sx={{ mb: 1.5 }}
        >
          <AlertTitle sx={{ fontWeight: 600, mb: 0.25 }}>
            {newlyIntroducedItems.length} newly introduced critical instance{newlyIntroducedItems.length !== 1 ? 's' : ''}
          </AlertTitle>
          {newlyIntroducedItems.length === 1
            ? `1 item was added in the last ${NEWLY_INTRODUCED_DAYS} days and is still active — a new client may have recently started facing this memory problem.`
            : `${newlyIntroducedItems.length} items were added in the last ${NEWLY_INTRODUCED_DAYS} days and are still active — new clients may have recently started facing these memory problems.`}
        </Alert>
      )}



      {/* Group-by-Release summary cards */}
      {groupByRelease && itemsByRelease.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
          {itemsByRelease.map(([release, releaseItems]) => {
            const done = releaseItems.filter((i) => COMPLETED_STATES_SET.has(i.state)).length;
            const total = releaseItems.length;
            const pct = total > 0 ? Math.round((done / total) * 100) : 0;
            return (
              <Paper
                key={release}
                variant="outlined"
                sx={{
                  px: 2, py: 1.5, borderRadius: 2, minWidth: 150, cursor: 'pointer',
                  borderColor: releaseFilter === release ? DARK_GREEN : 'divider',
                  bgcolor: releaseFilter === release ? `${DARK_GREEN}08` : 'background.paper',
                  '&:hover': { borderColor: DARK_GREEN, bgcolor: `${DARK_GREEN}05` }
                }}
                onClick={() => setReleaseFilter(releaseFilter === release ? '' : release)}
              >
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  {release === 'Unscheduled' ? 'Unscheduled' : `v${release}`}
                </Typography>
                <Typography variant="h5" fontWeight={700} sx={{ color: DARK_GREEN, lineHeight: 1.2, mt: 0.25 }}>
                  {total}
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 400, ml: 0.5 }}>fixes</Typography>
                </Typography>
                <Typography variant="caption" color="text.secondary">{done} done · {pct}%</Typography>
                <LinearProgress
                  variant="determinate"
                  value={pct}
                  sx={{ mt: 0.75, height: 4, borderRadius: 1, bgcolor: `${DARK_GREEN}15`, '& .MuiLinearProgress-bar': { bgcolor: DARK_GREEN } }}
                />
              </Paper>
            );
          })}
        </Box>
      )}

      {/* Inline filters above the PBIs table */}
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Fix Version</InputLabel>
          <Select value={releaseFilter} onChange={(e) => setReleaseFilter(e.target.value)} label="Fix Version">
            <MenuItem value="">All Releases</MenuItem>
            {uniqueReleases.map((r) => (
              <MenuItem key={r} value={r}>{r}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Client</InputLabel>
          <Select value={clientFilter} onChange={(e) => setClientFilter(e.target.value)} label="Client">
            <MenuItem value="">All Clients</MenuItem>
            {uniqueClients.map((c) => (
              <MenuItem key={c} value={c}>{c}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {(releaseFilter || clientFilter) && (
          <Chip
            label="Clear filters"
            size="small"
            variant="outlined"
            onClick={() => { setReleaseFilter(''); setClientFilter(''); }}
            sx={{ cursor: 'pointer' }}
          />
        )}
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          {filteredItems.length} PBI{filteredItems.length !== 1 ? 's' : ''}
        </Typography>
      </Box>

      {/* PBIs table */}
      <TableContainer component={Paper} sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, '& .MuiTable-root': { minWidth: 700 } }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 48, paddingRight: 0 }} />
              <TableCell>ADO ID</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Fix Version</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>State</TableCell>
              <TableCell align="right">Story Points</TableCell>
              <TableCell align="right">Completion</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    {items.length === 0
                      ? 'No PBIs found. Ensure the ADO webhook sync has run and Feature 919722 has child work items.'
                      : 'No PBIs match the current filter.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              (() => {
                const renderPbiRow = (pbi) => {
                  const pbiIdStr = String(pbi.id || '');
                  const isExpanded = expandedItems.has(pbiIdStr);
                  const children = itemChildren[pbiIdStr];
                  const isLoadingChildren = loadingChildren.has(pbiIdStr);
                  return (
                    <React.Fragment key={pbi.id}>
                      <TableRow
                        hover
                        onClick={() => toggleExpand(pbi.id)}
                        sx={{
                          cursor: 'pointer',
                          ...(isNewlyIntroduced(pbi) && {
                            bgcolor: '#fff3e0',
                            '&:hover': { bgcolor: '#ffe0b2' }
                          })
                        }}
                      >
                        <TableCell sx={{ width: 48, paddingRight: 0, verticalAlign: 'middle' }} onClick={(e) => e.stopPropagation()}>
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); toggleExpand(pbi.id); }}
                            aria-label={isExpanded ? 'Collapse' : 'Expand'}
                          >
                            {isLoadingChildren
                              ? <CircularProgress size={20} />
                              : isExpanded
                                ? <ExpandLessIcon fontSize="small" />
                                : <ChevronRightIcon fontSize="small" />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          {pbi.id ? (
                            <Box
                              component="a"
                              href={getAdoUrl(pbi.id)}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'primary.main', textDecoration: 'none', fontWeight: 500, '&:hover': { textDecoration: 'underline', color: 'primary.dark' } }}
                            >
                              {pbi.id}
                              <OpenInNewIcon sx={{ fontSize: '0.875rem' }} />
                            </Box>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          {pbi.clientName && (
                            <Chip
                              label={pbi.clientName}
                              size="small"
                              variant="outlined"
                              onClick={(e) => { e.stopPropagation(); setClientFilter(pbi.clientName); }}
                              sx={{ fontSize: '0.7rem', cursor: 'pointer' }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                            {isNewlyIntroduced(pbi) && (
                              <Chip
                                label="NEW"
                                size="small"
                                color="warning"
                                icon={<NewReleasesIcon sx={{ fontSize: '0.8rem !important' }} />}
                                sx={{ fontSize: '0.65rem', height: 20, fontWeight: 700, flexShrink: 0 }}
                              />
                            )}
                            <Tooltip title={pbi.title || ''}>
                              <Typography variant="body2" sx={{ maxWidth: 340 }}>{pbi.title}</Typography>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          {pbi.fixVersion ? (
                            <Chip
                              label={pbi.fixVersion}
                              size="small"
                              variant="outlined"
                              onClick={(e) => { e.stopPropagation(); setReleaseFilter(pbi.fixVersion); }}
                              sx={{ fontSize: '0.7rem', cursor: 'pointer', borderColor: DARK_GREEN, color: DARK_GREEN }}
                            />
                          ) : (
                            <Typography variant="caption" color="text.disabled">—</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {pbi.workItemType && (
                            <Chip label={pbi.workItemType} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                          )}
                        </TableCell>
                        <TableCell>
                          {pbi.state && (
                            <Chip label={pbi.state} size="small" variant="outlined" color={STATE_CHIP_COLOR(pbi.state)} />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {pbi.completedStoryPoints} / {pbi.totalStoryPoints}
                        </TableCell>
                        <TableCell align="right">
                          <CompletionDonut percentage={pbi.completionRate} />
                        </TableCell>
                      </TableRow>

                      {isExpanded && children && children.length > 0 && children.map((task) => (
                        <TableRow key={task.id} hover sx={{ backgroundColor: 'grey.50', '&:hover': { backgroundColor: 'grey.100' } }}>
                          <TableCell sx={{ width: 48, paddingRight: 0, paddingLeft: 2 }} />
                          <TableCell>
                            <Box
                              component="a"
                              href={getAdoUrl(task.id)}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'primary.main', textDecoration: 'none', fontWeight: 500, pl: 3, '&:hover': { textDecoration: 'underline', color: 'primary.dark' } }}
                            >
                              {task.id}
                              <OpenInNewIcon sx={{ fontSize: '0.875rem' }} />
                            </Box>
                          </TableCell>
                          <TableCell />
                          <TableCell sx={{ pl: 3 }}>
                            <Typography variant="body2">{task.title}</Typography>
                          </TableCell>
                          <TableCell />
                          <TableCell>
                            <Chip label="Task" size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                          </TableCell>
                          <TableCell>
                            {task.state && (
                              <Chip label={task.state} size="small" variant="outlined" color={STATE_CHIP_COLOR(task.state)} />
                            )}
                          </TableCell>
                          <TableCell align="right">{task.storyPoints ?? '-'}</TableCell>
                          <TableCell />
                        </TableRow>
                      ))}

                      {isExpanded && children && children.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={9} sx={{ pl: 8, py: 1, color: 'text.secondary', fontSize: '0.8125rem' }}>
                            No tasks found for this PBI.
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                };

                if (!groupByRelease) {
                  return filteredItems.map(renderPbiRow);
                }

                return itemsByRelease.flatMap(([release, releaseItems]) => {
                  const done = releaseItems.filter((i) => COMPLETED_STATES_SET.has(i.state)).length;
                  return [
                    <TableRow key={`group-${release}`} sx={{ bgcolor: `${DARK_GREEN}10` }}>
                      <TableCell colSpan={9} sx={{ py: 0.75, px: 2 }}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Typography variant="subtitle2" fontWeight={700} sx={{ color: DARK_GREEN }}>
                            {release === 'Unscheduled' ? 'Unscheduled' : `Fix Version: ${release}`}
                          </Typography>
                          <Chip
                            label={`${releaseItems.length} fix${releaseItems.length !== 1 ? 'es' : ''}`}
                            size="small"
                            sx={{ bgcolor: DARK_GREEN, color: 'white', fontWeight: 700, fontSize: '0.7rem', height: 20 }}
                          />
                          <Chip
                            label={`${done} done`}
                            size="small"
                            color={done === releaseItems.length ? 'success' : 'default'}
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: 20 }}
                          />
                        </Stack>
                      </TableCell>
                    </TableRow>,
                    ...releaseItems.map(renderPbiRow)
                  ];
                });
              })()
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Sprint drill-down modal */}
      <Dialog
        open={sprintModal.open}
        onClose={() => setSprintModal((s) => ({ ...s, open: false }))}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1, pr: 6 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Chip
              size="small"
              label={sprintModal.type === 'created' ? 'Created' : 'Resolved'}
              sx={{
                bgcolor: sprintModal.type === 'created' ? '#1565C015' : '#388E3C15',
                color: sprintModal.type === 'created' ? '#1565C0' : '#2E7D32',
                fontWeight: 600
              }}
            />
            <Typography variant="subtitle1" fontWeight={600}>
              Sprint: {sprintModal.sprintLabel}
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {sprintModal.tickets.length} ticket{sprintModal.tickets.length !== 1 ? 's' : ''}{' '}
            {sprintModal.type === 'created' ? 'created' : 'resolved'} in this sprint
          </Typography>
          <IconButton
            onClick={() => setSprintModal((s) => ({ ...s, open: false }))}
            sx={{ position: 'absolute', top: 10, right: 10 }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {sprintModal.tickets.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary">No tickets found for this sprint.</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell sx={{ fontWeight: 600, width: 90 }}>ID</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 110 }}>State</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 100 }}>Client</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 110 }}>Fix Version</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sprintModal.tickets.map((t) => (
                    <TableRow key={t.id} hover>
                      <TableCell>
                        <Box
                          component="a"
                          href={getAdoUrl(t.id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ color: 'primary.main', textDecoration: 'none', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}
                        >
                          #{t.id}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ lineHeight: 1.4 }}>{t.title}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={t.state}
                          size="small"
                          color={STATE_CHIP_COLOR(t.state)}
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{t.clientName || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{t.fixVersion || 'Unscheduled'}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2 }}>
          <Button size="small" onClick={() => setSprintModal((s) => ({ ...s, open: false }))}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MemoryImprovementsTab;
